import { useState, useCallback, useRef, useEffect } from 'react';
import { useFocusEffect } from '@react-navigation/native';
import { NC_FILE_SELECT } from '@components/search/components/select';
import { PAGE_SIZE } from '@components/operatorPanel/components/OperatorPanelInformation/components/OperatorPanelInformationSearchNCFilesScreen/constants';
import { OperatorPanelInformationSearchNCFilesScreenProps } from '@components/operatorPanel/components/OperatorPanelInformation/components/OperatorPanelInformationSearchNCFilesScreen/types';
import { TABLE_NAMES } from '@v2/pages/common/constants/base.constants';
import { useGridPersistence } from '@v2/pages/common/hooks/useGridPersistence';

interface UseNCFilesSearchParams extends Pick<OperatorPanelInformationSearchNCFilesScreenProps, 'ncFilesSearch'> {
  machineFilter?: string;
  isFilterReady?: boolean;
  isEmptyFilter?: boolean;
}

export const useNCFilesSearch = ({ ncFilesSearch, machineFilter, isFilterReady = true, isEmptyFilter = false }: UseNCFilesSearchParams) => {
  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<string | null>(null);
  const [currentODataFilter, setCurrentODataFilter] = useState<string | null>(null);
  const [displayFacets, setDisplayFacets] = useState<Record<string, any> | null>(null);
  const [facetsLoaded, setFacetsLoaded] = useState(false);

  const machineFilterRef = useRef(machineFilter);
  const hasFetchedRef = useRef(false);

  const { orderByRef, gridODataFilterRef, handleGridSearchParams } = useGridPersistence(TABLE_NAMES.operatorPanelSearchNCFiles);

  useEffect(() => {
    machineFilterRef.current = machineFilter;
  }, [machineFilter]);

  const fetchNCFiles = useCallback(async (searchTerm: string = '', pageNum: number = 1, statusODataFilter: string | null = null) => {
    if (isEmptyFilter) {
      setSearchResults([]);
      setTotalCount(0);
      setDisplayFacets(null);
      setLoading(false);
      setFacetsLoaded(true);
      return;
    }
    setLoading(true);
    if (pageNum === 1) {
      setFacetsLoaded(false);
    }
    try {
      const gridFilter = gridODataFilterRef.current;
      const combinedFilter = [machineFilterRef.current, statusODataFilter, gridFilter]
        .filter(Boolean)
        .join(' and ') || null;

      const result = await ncFilesSearch({
        data: {
          search: searchTerm,
          top: PAGE_SIZE,
          skip: (pageNum - 1) * PAGE_SIZE,
        },
        filter: {
          oDataFilter: combinedFilter,
          orderBy: orderByRef.current.length ? orderByRef.current : undefined,
        },
        select: NC_FILE_SELECT,
        resetHistory: pageNum === 1,
      });

      if (result?.searchResult) {
        if (pageNum === 1) {
          setSearchResults(result.searchResult);
        } else {
          setSearchResults((prev) => [...prev, ...result.searchResult]);
        }
        setTotalCount(result.totalResultCount || 0);
        if (!statusODataFilter) {
          setDisplayFacets(result.facets || null);
        }
      }
    } catch (error) {
      console.error('Error fetching NC files:', error);
    } finally {
      setLoading(false);
      setFacetsLoaded(true);
    }
  }, [ncFilesSearch, isEmptyFilter]);

  // Single fetch trigger - waits for filter to be ready
  useFocusEffect(
    useCallback(() => {
      if (!isFilterReady) return;

      setPage(1);
      setSelectedStatusFilter(null);
      setCurrentODataFilter(null);
      fetchNCFiles('', 1);
      hasFetchedRef.current = true;
    }, [fetchNCFiles, isFilterReady])
  );

  // Refetch when machineFilter changes (only after initial fetch)
  useEffect(() => {
    if (!hasFetchedRef.current || !isFilterReady) return;

    setPage(1);
    setSelectedStatusFilter(null);
    setCurrentODataFilter(null);
    fetchNCFiles('', 1);
  }, [machineFilter]);

  const handleFilterChange = useCallback((filterName: string | null, oDataFilter: string | null) => {
    setSelectedStatusFilter(filterName);
    setCurrentODataFilter(oDataFilter);
    setPage(1);
    fetchNCFiles(searchText, 1, oDataFilter);
  }, [searchText, fetchNCFiles]);

  const handleSearch = useCallback(() => {
    setPage(1);
    fetchNCFiles(searchText, 1, currentODataFilter);
  }, [searchText, currentODataFilter, fetchNCFiles]);

  const handleLoadMore = useCallback(() => {
    if (!loading && searchResults.length < totalCount) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchNCFiles(searchText, nextPage, currentODataFilter);
    }
  }, [loading, searchResults.length, totalCount, page, searchText, currentODataFilter, fetchNCFiles]);

  const handleCancelSearch = useCallback(() => {
    setSearchText('');
    setSelectedStatusFilter(null);
    setCurrentODataFilter(null);
    fetchNCFiles('', 1, null);
  }, [fetchNCFiles]);

  const gridSearch = useCallback(
    async ({ orderBy, oDataFilter }: { orderBy?: string[]; oDataFilter?: string | null }) => {
      handleGridSearchParams({ orderBy, oDataFilter });
      setPage(1);
      await fetchNCFiles(searchText, 1, currentODataFilter);
    },
    [fetchNCFiles, searchText, currentODataFilter, handleGridSearchParams]
  );

  return {
    searchText,
    setSearchText,
    searchResults,
    totalCount,
    loading,
    facets: displayFacets,
    facetsLoaded,
    selectedStatusFilter,
    handleSearch,
    handleLoadMore,
    handleFilterChange,
    handleCancelSearch,
    gridSearch,
  };
};
